import { getI18nLang } from '@yandex-int/i18n';
import { OAuthAppSettings, UpdateOAuthAppSettings } from 'client/model/oauth';
import { Operation } from 'client/model/operation';
import { IQuickApp, TQuickAppsMeta } from 'client/model/privateApps';
import { UserReview } from 'client/model/reviews';
import { DashboardRowMeta, MonitoringQuery, SolomonLabels } from 'client/model/solomon';
import { ItemsPageContinues } from 'client/types/utils';
import { getIsYangoRequest } from 'client/utils';
import queryString from 'query-string';

import { routes } from '../lib/routes';
import { InstallableAppSettings } from '../model/settings';
import { Channel, IAppCard, IInstallableAppPageInfo, InstallableApp, Skill } from '../model/skill';
import { ApkInfoWithTargets, AppsMetaInfo, FeatureFlags, LicenseAgreement } from '../types';

import { callApi, callPublicApi, translateOperations } from './utils';

export function getAppsMeta() {
    return callApi<AppsMetaInfo>({ method: 'GET', url: routes.app.appStoreApi('apps/meta-info') });
}

export function getAppsCards({
    page,
    pageSize,
    channels,
    listNotOwnedApps,
    onlyPublished,
    query,
}: {
    page?: number;
    pageSize?: number;
    channels: Channel[];
    listNotOwnedApps?: boolean;
    onlyPublished?: boolean;
    query?: string;
}) {
    return callApi<{ cards: IAppCard[]; paging_info: IInstallableAppPageInfo }>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/cards`),
        queryParams: {
            page,
            pageSize,
            channels,
            listNotOwnedApps,
            onlyPublished,
            query,
        },
    });
}

export function createApp<T extends Channel>(appName: string, channel: T) {
    return callApi<T extends Channel.TvApp | Channel.AutoApp | Channel.SmartScreenApp ? { skillId: string } : Skill>({
        method: 'POST',
        url: routes.app.appStoreApi('/apps'),
        data: {
            channel,
            language: getI18nLang(),
            isYangoConsole: getIsYangoRequest(),
            appName,
        },
    });
}

export function deleteApp(id: string, channel: string) {
    const appIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`/apps/${appIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function stopApp(id: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillIdURLEncoded}/stop`),
        queryParams: { channel: channel },
    });
}

export function forceStopApp(id: string, channel: Channel, data: { comment: string }) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillIdURLEncoded}/force-stop`),
        data,
        queryParams: { channel: channel },
    });
}

export function getInstallableApp(id: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function updateInstallableAppDraft(id: string, data: Partial<InstallableAppSettings>) {
    const skillIdURLEncoded = encodeURIComponent(id);
    return callApi<InstallableApp>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/update`),
        data,
    });
}

export function installableAppUploadImage(
    skillId: string,
    channel: Channel,
    file: File,
    imageType: 'appTileImage' | 'appScreenshot',
) {
    const skillIdURLEncoded = encodeURIComponent(skillId);
    const form = new FormData();

    form.append('file', file);

    return callApi<{ id: string; url: string }>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/upload-image`),
        data: form,
        queryParams: {
            channel: channel,
            type: imageType,
        },
    });
}

export function installableAppUploadApk(params: {
    appId: string;
    channel: Channel;
    file: File;
    versionNotes?: string;
}) {
    const form = new FormData();

    const versionNotesIfPresent = params.versionNotes ? encodeURIComponent(params.versionNotes) : undefined;
    const appId = encodeURIComponent(params.appId);

    form.append('file', params.file);

    return callApi<ApkInfoWithTargets>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${appId}/draft/upload-apk`),
        queryParams: {
            channel: params.channel,
            versionNotes: versionNotesIfPresent,
        },
        data: form,
    });
}

export function installableAppUploadLicenseAgreement(skillId: string, channel: Channel, file: File) {
    const form = new FormData();

    form.append('file', file);

    return callApi<LicenseAgreement>({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillId}/draft/upload-draft-user-agreement`),
        data: form,
        queryParams: {
            channel: channel,
        },
    });
}

export function requestInstallableAppReview(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/request-moderation`),
        queryParams: { channel: channel },
    });
}

export function requestCancelInstallableAppModeration(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/cancel-moderation`),
        queryParams: { channel: channel },
    });
}

export function requestInstallableAppDeploy(skillId: string, channel: Channel) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi({
        method: 'POST',
        url: routes.app.appStoreApi(`apps/${skillIdURLEncoded}/draft/request-deploy`),
        queryParams: { channel: channel },
    });
}

export function getFeatureFlags(skillId: string, channel: Channel): Promise<FeatureFlags> {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callApi<FeatureFlags>({
        method: 'GET',
        url: routes.app.appStoreApi(`feature-flags/${skillIdURLEncoded}`),
        queryParams: { channel: channel },
    });
}

export function updateSkillFeatureFlags(skillId: string, channel: Channel, data: string[]) {
    const skillIdURLEncoded = encodeURIComponent(skillId);

    return callPublicApi<object>({
        method: 'PUT',
        url: routes.app.appStoreApi(`skill-feature-flags/${skillIdURLEncoded}`),
        data,
        queryParams: { channel: channel },
    });
}

export function updateUserFeatureFlags(userId: string, channel: Channel, data: Record<string, boolean>) {
    return callPublicApi<object>({
        method: 'PUT',
        url: routes.app.appStoreApi(`user-feature-flags/${userId}`),
        data,
        queryParams: { channel: channel },
    });
}

export function createOrUpdateOAuthApp(data: UpdateOAuthAppSettings) {
    return callApi<OAuthAppSettings>({ method: 'POST', url: routes.app.appStoreApi('oauth/apps'), data });
}

export function getOAuthApp(id: string) {
    return callApi<OAuthAppSettings>({ method: 'GET', url: routes.app.appStoreApi(`oauth/apps/${id}`) });
}

export function getMonitoringData(skillId: string, query: MonitoringQuery) {
    return callApi<DashboardRowMeta[]>({
        method: 'GET',
        url: routes.app.appStoreApi(`/monitoring/apps/${skillId}`, `?${queryString.stringify(query)}`),
        timeout: __appConfig.monitoring.timeoutMs,
    });
}

export function getMonitoringLabels() {
    return callApi<SolomonLabels>({ method: 'GET', url: routes.app.appStoreApi('/monitoring/page-data') });
}

export function getSkillReviews(skillId: string, channel: Channel, page: number) {
    return callApi<ItemsPageContinues<UserReview>>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillId}/reviews`),
        queryParams: {
            channel: channel,
            page: page,
        },
    });
}

export async function getOperationsTranslated(skillId: string) {
    const response = await callApi<Array<Operation>>({
        method: 'GET',
        url: routes.app.appStoreApi(`apps/${skillId}/operations`),
    });

    return translateOperations(response);
}

export function apiSetActiveRevision<T extends Channel>(skillId: string, channel: T, revisionId: string) {
    return callApi<InstallableApp>({
        method: 'POST',
        url: routes.app.appStoreApi(`/apps/${skillId}/set-active-revision`),
        data: {
            revisionId,
        },
        queryParams: {
            channel: channel,
        },
    });
}

export function getQuickApps() {
    return callApi<IQuickApp[]>({
        method: 'GET',
        url: routes.app.appStoreApi('quick-apps/apps'),
    });
}

export function deleteQuickApp(id: string, channel: string) {
    const appIdURLEncoded = encodeURIComponent(id);
    return callApi<void>({
        method: 'DELETE',
        url: routes.app.appStoreApi('quick-apps/app'),
        queryParams: { id: appIdURLEncoded, channel },
    });
}

export function getQuickAppsMetaInfo() {
    return callApi<{
        storage_info: TQuickAppsMeta;
    }>({
        method: 'GET',
        url: routes.app.appStoreApi('quick-apps/meta-info'),
    });
}

export function quickAppUploadApk(channel: Channel, file: File) {
    const form = new FormData();

    form.append('file', file);

    return callApi<string>({
        method: 'POST',
        url: routes.app.appStoreApi('quick-apps/upload-apk'),
        queryParams: {
            channel,
        },
        data: form,
    });
}
